/**
 * The management dashboard — «چطور داریم پیش می‌رویم؟».
 *
 * FOUR SECTIONS, mirroring docs/kpi-catalogue.md: the funnel, why deals fail,
 * what the market looks like, and how the team is doing. The last is ADMIN
 * only, and that is a product decision rather than a security one: a USER sees
 * their own row because the analytics service narrows them to it.
 *
 * TWO RULES SHAPE EVERY CARD ON THIS PAGE.
 *
 * 1. **The ⓘ comes from the server.** Formula, grain and caveat all arrive with
 *    the number, from the one catalogue. There is no Persian formula text in
 *    any component here.
 * 2. **Every number is clickable.** A dashboard that shows «۱۲ مخاطب ریزش
 *    کردند» and cannot say WHICH twelve is a dashboard that ends the
 *    conversation instead of starting it.
 *
 * The sections are collapsible, and that is a feature rather than a
 * concession: a dashboard with four cards people read beats one with forty
 * they scroll past, and hiding a section they never use is the right answer.
 */
import { Button, Card, EmptyState, Spinner } from '@meshhub/design-tokens/ui';
import { useMutation, useQuery } from '@apollo/client';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CohortTable } from '../components/dashboard/CohortTable.web.jsx';
import { DistributionCard } from '../components/dashboard/DistributionCard.web.jsx';
import { MetricCard } from '../components/dashboard/MetricCard.web.jsx';
import { FormDialog } from '../components/FormKit.web.jsx';
import { formatCount, toPersianDigits } from '../lib/format.js';
import { PRESETS, describeRange, resolvePreset, shiftRange } from '../lib/periods.js';
import { DRILL_DOWN_QUERY, METRICS_QUERY } from '../lib/queries.js';
import { useSession } from '../lib/session.jsx';
import { useShortcut } from '../lib/shortcuts.jsx';
import { S } from '../lib/strings.js';

/**
 * Which metrics each section asks for, in one place.
 *
 * Named here rather than assembled per card so the page makes ONE round trip
 * for twenty numbers. Twenty sequential queries is twenty round trips before
 * anything paints.
 */
const SECTIONS = [
  {
    id: 'funnel',
    scalars: ['conversion_rate', 'velocity', 'median_time_in_stage'],
    distributions: ['contacts_by_status'],
    cohort: 'success_percentage',
    trend: 'success_trend',
  },
  {
    id: 'failure',
    scalars: ['churn_rate', 'missed_activity_rate', 'issues_per_subscriber'],
    distributions: ['backward_transitions', 'stalled_contacts', 'suggestion_outcomes'],
  },
  {
    id: 'market',
    scalars: ['reachability', 'affiliation_coverage'],
    distributions: [
      'contacts_by_field_of_work',
      'contacts_by_specialty',
      'contacts_by_gender',
      'subscribers_by_subscription_type',
      'product_interest',
      'interest_to_subscription',
      'new_contacts',
    ],
  },
  {
    id: 'team',
    adminOnly: true,
    scalars: [],
    distributions: ['user_activity_summary', 'campaign_performance', 'automation_performance'],
  },
];

const PRESET_LABELS = {
  this_month: S.dashboard.thisMonth,
  this_quarter: S.dashboard.thisQuarter,
  this_year: S.dashboard.thisYear,
  last_30: S.dashboard.last30,
};

const BUCKET_LABELS = {
  DAY: S.dashboard.bucketDay,
  WEEK: S.dashboard.bucketWeek,
  MONTH: S.dashboard.bucketMonth,
  QUARTER: S.dashboard.bucketQuarter,
};

function Section({ id, children, count }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-2 text-start text-base font-bold text-text"
      >
        {open ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
        {S.dashboard.sections[id]}
        <span className="text-xs font-normal text-text-muted">({formatCount(count)})</span>
      </button>
      {open && children}
    </section>
  );
}

export function DashboardScreen() {
  const { isAdmin } = useSession();
  const [preset, setPreset] = useState('this_quarter');
  const [range, setRange] = useState(() => resolvePreset('this_quarter'));
  const [bucket, setBucket] = useState('MONTH');
  const [drill, setDrill] = useState(null);

  const sections = useMemo(
    () => SECTIONS.filter((s) => !s.adminOnly || isAdmin),
    [isAdmin],
  );

  const names = useMemo(() => {
    const out = new Set(['system_health']);
    for (const s of sections) {
      s.scalars.forEach((n) => out.add(n));
      s.distributions.forEach((n) => out.add(n));
      if (s.cohort) out.add(s.cohort);
      if (s.trend) out.add(s.trend);
    }
    return [...out];
  }, [sections]);

  const filter = useMemo(
    () => ({ dateFrom: range.dateFrom, dateTo: range.dateTo, bucket }),
    [range, bucket],
  );

  const { data, loading, error, refetch } = useQuery(METRICS_QUERY, {
    variables: { names, filter },
    // The dashboard is read on arrival and left open. Refetching on focus
    // would re-run twenty queries every time somebody alt-tabs back.
    fetchPolicy: 'cache-and-network',
  });

  const applyPreset = useCallback((next) => {
    setPreset(next);
    setRange(resolvePreset(next));
  }, []);

  const shift = useCallback(
    (direction) => setRange((r) => shiftRange(r, preset, direction)),
    [preset],
  );

  useShortcut('r', () => refetch(), {
    description: S.dashboard.refresh,
    group: S.dashboard.title,
  });
  useShortcut('[', () => shift(-1), {
    description: S.dashboard.previousPeriod,
    group: S.dashboard.title,
  });
  useShortcut(']', () => shift(1), {
    description: S.dashboard.nextPeriod,
    group: S.dashboard.title,
  });

  const byName = useMemo(() => {
    const map = new Map();
    for (const m of data?.metrics?.metrics ?? []) map.set(m.name, m);
    return map;
  }, [data]);

  const errors = useMemo(() => {
    const map = new Map();
    for (const e of data?.metrics?.errors ?? []) map.set(e.name, e);
    return map;
  }, [data]);

  // Ingest lag, shown as a banner rather than buried on the ops panel: a
  // business number computed from a read model three days behind is a wrong
  // number that looks right, and the person who needs to know is whoever is
  // reading it (docs/kpi-catalogue.md §4).
  const health = byName.get('system_health');
  const staleSeconds = Math.max(
    0,
    ...(health?.rows ?? []).map((r) => r.values?.[0] ?? 0),
  );
  const isStale = staleSeconds > 600;

  const openDrill = useCallback((metric, key) => {
    setDrill({ metric: metric.name, key, title: metric.definition.titleFa });
  }, []);

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-bold text-text">{S.dashboard.title}</h1>
          <p className="text-xs text-text-muted">{describeRange(range, preset)}</p>
          {/*
            The five conventions every number below rests on. A DISCLOSURE
            rather than a banner: they do not change, so a permanent notice
            would be read once and then stop being seen — but they are not
            findable at all if they live only in a document. Each is flagged
            CONFIRM in the KPI catalogue exactly like the per-metric
            assumptions the cards badge; these simply belong to no one card.
          */}
          <details className="mt-1">
            <summary className="cursor-pointer text-xs text-text-muted hover:text-text">
              {S.dashboard.conventions}
            </summary>
            <div className="mt-1 rounded border border-border bg-surface p-3 text-xs text-text-muted">
              <p className="mb-1">{S.dashboard.conventionsHint}</p>
              <ul className="list-disc space-y-1 pe-4">
                {S.dashboard.conventionsList.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <p className="mt-2 font-mono">{S.dashboard.conventionsSource}</p>
            </div>
          </details>
        </div>

        <div className="flex flex-wrap items-center gap-1">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPreset(p)}
              aria-pressed={preset === p}
              className={`rounded-full border px-3 py-1 text-xs ${
                preset === p
                  ? 'border-primary bg-primary text-primary-contrast'
                  : 'border-border text-text-muted hover:bg-surface-hover'
              }`}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-xs text-text-muted">
          {S.dashboard.bucket}
          <select
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            className="h-control rounded border border-border bg-bg px-2 text-xs"
          >
            {Object.entries(BUCKET_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <Button variant="ghost" onClick={() => refetch()} aria-label={S.dashboard.refresh}>
          <RefreshCw size={14} aria-hidden="true" />
          {S.dashboard.refresh}
        </Button>
      </header>

      {isStale && (
        <div className="rounded border border-warning bg-warning-subtle p-3 text-sm text-text">
          {S.dashboard.staleWarning}{' '}
          <Link to="/settings/system" className="text-primary hover:underline">
            {S.ops.title}
          </Link>
        </div>
      )}

      {loading && !data && <Spinner />}
      {error && <EmptyState title={error.message} />}

      {data &&
        sections.map((section) => {
          const cards = [...section.scalars, ...section.distributions];
          return (
            <Section key={section.id} id={section.id} count={cards.length}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {section.scalars.map((name) => {
                  const metric = byName.get(name);
                  const err = errors.get(name);
                  if (!metric && !err) return null;
                  return (
                    <MetricCard
                      key={name}
                      metric={metric}
                      error={err}
                      onDrillDown={metric ? openDrill : undefined}
                    />
                  );
                })}
                {section.distributions.map((name) => {
                  const metric = byName.get(name);
                  const err = errors.get(name);
                  if (err) return <MetricCard key={name} error={err} />;
                  if (!metric) return null;
                  return (
                    <DistributionCard key={name} metric={metric} onDrillDown={openDrill} />
                  );
                })}
              </div>

              {section.cohort && byName.get(section.cohort) && (
                <Card className="p-2">
                  <h3 className="p-2 text-sm font-medium text-text">
                    {byName.get(section.cohort).definition.titleFa}
                  </h3>
                  <CohortTable metric={byName.get(section.cohort)} />
                </Card>
              )}
            </Section>
          );
        })}

      <DrillDownDialog drill={drill} filter={filter} onClose={() => setDrill(null)} />
    </div>
  );
}

/**
 * The contacts behind a number.
 *
 * The ids come from analytics, which holds no names; the names are composed in
 * the BFF through the CRM loader. This dialog is where that composition
 * becomes visible to a user, and it is the reason the drill-down exists at
 * all: «۱۲ مخاطب ریزش کردند» without a list is a fact nobody can act on.
 */
function DrillDownDialog({ drill, filter, onClose }) {
  const { data, loading } = useQuery(DRILL_DOWN_QUERY, {
    variables: { metric: drill?.metric, key: drill?.key ?? '', filter, limit: 100 },
    skip: !drill,
  });
  if (!drill) return null;
  const result = data?.metricDrillDown;
  return (
    <FormDialog
      open
      onOpenChange={onClose}
      title={`${S.dashboard.drillDownTitle} — ${drill.title}`}
      wide
    >
      {loading && <Spinner />}
      {result && result.contacts.length === 0 && (
        <EmptyState title={S.dashboard.drillDownEmpty} />
      )}
      {result && result.contacts.length > 0 && (
        <>
          <p className="pb-2 text-xs text-text-muted">
            {S.dashboard.showing} {formatCount(result.contacts.length)} {S.dashboard.of}{' '}
            {formatCount(result.total)}
          </p>
          <ul className="flex flex-col divide-y divide-border">
            {result.contacts.map((c) => (
              <li key={c.id} className="py-2">
                <Link
                  to={`/contacts/${c.id}`}
                  className="flex items-center justify-between gap-2 hover:underline"
                >
                  <span className="text-sm text-text">{c.displayName}</span>
                  <span className="text-xs text-text-muted" dir="ltr">
                    {c.primaryPhoneE164 ? toPersianDigits(c.primaryPhoneE164) : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </FormDialog>
  );
}
